import { useEffect, useState } from "react";
import { Button, Card, Col, Descriptions, Row, Select, Table, Tag } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MongerlabCustomerService from "../services/MongerlabCustomerService";
import { IMongerlabCustomer, IMongerlabCustomerStatusLog } from "../types/MongerlabCustomer";
import { convertDate, openNotification, can } from "../utils";
import { CopyClipboard } from "../components/General/CopyClipboard";
import { StartCall } from "../components/General/StartCall";
import Notes from "../components/MongerlabCustomerDetail/Notes";

const MongerlabCustomerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState<IMongerlabCustomer | null>(null);
  const [statusLogs, setStatusLogs] = useState<IMongerlabCustomerStatusLog[]>([]);
  const [crmStatusOptions, setCrmStatusOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const canUpdateCrmStatus = can("mongerlab-customers:crm-status:update");

  const loadData = () => {
    if (!id) return;
    setLoading(true);
    const requests: Promise<unknown>[] = [
      MongerlabCustomerService.getById(Number(id)),
      MongerlabCustomerService.getStatusLogs(Number(id)),
    ];
    if (canUpdateCrmStatus) {
      requests.push(MongerlabCustomerService.getCrmStatuses());
    }
    Promise.all(requests)
      .then((results) => {
        const customerRes = results[0] as Awaited<ReturnType<typeof MongerlabCustomerService.getById>>;
        const logsRes = results[1] as Awaited<ReturnType<typeof MongerlabCustomerService.getStatusLogs>>;
        setCustomer(customerRes.data);
        setStatusLogs(logsRes.data);
        if (canUpdateCrmStatus && results[2]) {
          const statusesRes = results[2] as Awaited<ReturnType<typeof MongerlabCustomerService.getCrmStatuses>>;
          setCrmStatusOptions(statusesRes.data.map((i) => ({ value: i.id, label: i.name })));
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleChangeCrmStatus = (crmStatusId: number) => {
    MongerlabCustomerService.updateCrmStatus(Number(id), { crmStatusId })
      .then(() => {
        openNotification("success", "Success", "CRM Status updated");
        loadData();
      })
      .catch(console.error);
  };

  const handleBack = () => {
    navigate("/mongerlab-customers", { state: location.state });
  };

  return (
    <div>
      <Card style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginRight: 16 }}>
          Back
        </Button>
        <span style={{ fontSize: 20, fontWeight: 600 }}>{customer?.name}</span>
        {customer?.isDeleted && (
          <Tag color="red" style={{ marginLeft: 8 }}>
            Deleted
          </Tag>
        )}
      </Card>

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={14}>
          <Card title="Customer Information" loading={loading}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="App ID">{customer?.appId}</Descriptions.Item>
              <Descriptions.Item label="Name">{customer?.name}</Descriptions.Item>
              <Descriptions.Item label="Email">
                <CopyClipboard text={customer?.email || ""} />
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {(customer?.phone || "-")} <StartCall text={customer?.phone || ""} />
              </Descriptions.Item>
              <Descriptions.Item label="Role">{customer?.roleName}</Descriptions.Item>
              <Descriptions.Item label="CRM Status">
                {canUpdateCrmStatus ? (
                  <Select
                    value={customer?.crmStatusId}
                    options={crmStatusOptions}
                    style={{ minWidth: 200 }}
                    onChange={handleChangeCrmStatus}
                    showSearch
                    optionFilterProp="label"
                  />
                ) : (
                  customer?.crmStatusName || "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="App Status">{customer?.appStatusName}</Descriptions.Item>
              <Descriptions.Item label="Subscription Plan">{customer?.subsPlan || "-"}</Descriptions.Item>
              <Descriptions.Item label="Product Quantity">{customer?.productQuantity ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="Subscription Start">
                {customer?.subscriptionStartDate ? convertDate(customer.subscriptionStartDate) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Subscription End">
                {customer?.subscriptionEndDate ? convertDate(customer.subscriptionEndDate) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Register Date">
                {customer?.registerDate ? convertDate(customer.registerDate) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Last Login">
                {customer?.lastLoginDate ? convertDate(customer.lastLoginDate) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="User Account Count">{customer?.userAccountCount ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="Last Successful Sync">
                {customer?.lastSyncedAt ? convertDate(customer.lastSyncedAt) : "-"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="CRM Status History" style={{ marginTop: 12 }} loading={loading}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={statusLogs}
              columns={[
                { title: "Old Status", dataIndex: "oldStatus", key: "oldStatus" },
                { title: "New Status", dataIndex: "newStatus", key: "newStatus" },
                {
                  title: "User",
                  key: "user",
                  render: (_, row) => row.user?.username || "-",
                },
                {
                  title: "Date",
                  dataIndex: "createdAt",
                  key: "createdAt",
                  render: (v) => convertDate(v),
                },
              ]}
            />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          {customer && <Notes customerId={customer.id} />}
        </Col>
      </Row>
    </div>
  );
};

export default MongerlabCustomerDetail;
